import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { selectFolderItems } from "../../../common/Sidebar/redux/selectors";
import {
  clearRagObjects,
  toggleAccordion,
} from "../../../common/Sidebar/redux/slice";
import {
  createComparisonObjectAction,
  getComparisonObjectsAction,
  getModelsObjectAction,
  updateComparisonObjectsWithChildrenAction,
} from "../../../features/Comparison/redux/actions";
import {
  selectComparisonLoading,
  selectComparisonObjects,
  selectSelectedComparisonObject,
} from "../../../features/Comparison/redux/selectors";
import {
  setComparisonObjects,
  setComparisonObjectValue,
  setSelectedComparisonObject,
} from "../../../features/Comparison/redux/slice";
import type {
  CompareObject,
  ComparisonObjectValue,
} from "../../../services/api/instances/comparisonAPI/types";
import { createStorageObjectApi } from "../../../services/lib/storageObjectManager";
import { getServicePagePath } from "../../../services/navigation/navigateToRoute";
import { getComplianceObjectsAction } from "../../../services/redux/complianceSlice/actions";
import { selectCompliance } from "../../../services/redux/complianceSlice/selectors";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { getFilteredObjects } from "../../../shared/utils/objectStorageFunctions";
import { StorageObject } from "../../../services/api/instances/storageApi/types/object_types";

export const useComparisonPage = () => {
  const dispatch = useAppDispatch();
  const compliance = useAppSelector(selectCompliance);
  const selectedComparisonObject = useAppSelector(
    selectSelectedComparisonObject,
  );
  const comparisonObjects = useAppSelector(selectComparisonObjects);
  const isComparisonLoading = useAppSelector(selectComparisonLoading);
  const folderItems = useAppSelector(selectFolderItems);
  const { id } = useParams<{ id?: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const storage = useMemo(
    () => createStorageObjectApi<ComparisonObjectValue>(comparisonObjects),
    [comparisonObjects],
  );

  const sidebarComparisonItems = useMemo(() => {
    return storage.toMenuItems();
  }, [comparisonObjects]);

  useEffect(() => {
    if (!comparisonObjects) return;
    if (isComparisonLoading) return;
    if (!compliance) {
      dispatch(getComplianceObjectsAction());
    }
    dispatch(getModelsObjectAction());
    dispatch(getComparisonObjectsAction())
      .unwrap()
      .then((response: StorageObject<ComparisonObjectValue>[]) => {
        dispatch(setComparisonObjects(response));
        if (id === undefined && selectedComparisonObject) {
          navigate(
            getServicePagePath(selectedComparisonObject.id, "llm_compare"),
          );
          if (selectedComparisonObject?.value !== null) {
            dispatch(toggleAccordion(selectedComparisonObject.id));
          }
          dispatch(
            setComparisonObjectValue(
              selectedComparisonObject?.value?.llm_compare,
            ),
          );
        } else {
          const activeComparisonById = storage.findById(id as string);
          const activeComparison = activeComparisonById || response[0];
          if (!activeComparison) return;
          navigate(getServicePagePath(activeComparison.id, "llm_compare"));
          dispatch(
            setSelectedComparisonObject(
              activeComparison as StorageObject<ComparisonObjectValue>,
            ),
          );
          dispatch(
            setComparisonObjectValue(activeComparison?.value?.llm_compare),
          );
          dispatch(toggleAccordion(activeComparison.id));
        }
      });
  }, []);

  const addComparisonObject = () => {
    dispatch(clearRagObjects());
    const id = selectedComparisonObject?.id;
    const folder = storage.findParent(id as string);
    switch (true) {
      case selectedComparisonObject?.value === null || folder !== null: {
        const folderId = folder?.id ? folder?.id : id;
        dispatch(createComparisonObjectAction({ isFolder: "object" }))
          .unwrap()
          .then((response: StorageObject<ComparisonObjectValue>) => {
            dispatch(
              updateComparisonObjectsWithChildrenAction({
                childId: response.id,
                parentId: folderId as string,
              }),
            );
            const newComparison = storage.addItem(response, folderId);
            dispatch(setComparisonObjects(newComparison));
            dispatch(setSelectedComparisonObject(response));
            dispatch(setComparisonObjectValue(response.value.llm_compare));
            navigate(getServicePagePath(response.id, "llm_compare"));
            if (!folderItems.includes(folderId as string)) {
              dispatch(toggleAccordion(id as string));
            }
          });
        return;
      }
      case folder === null: {
        dispatch(createComparisonObjectAction({ isFolder: "object" }))
          .unwrap()
          .then((response: StorageObject<ComparisonObjectValue>) => {
            const newComparisonObjects = [...comparisonObjects, response];
            dispatch(setComparisonObjects(newComparisonObjects));
            dispatch(setSelectedComparisonObject(response));
            dispatch(setComparisonObjectValue(response.value.llm_compare));
            navigate(getServicePagePath(response.id, "llm_compare"));
          });
        return;
      }
      default: {
        return;
      }
    }
  };

  const addFolder = (name?: string) => {
    const postObj: {
      isFolder: "folder";
      name?: string;
      withoutCount?: boolean;
    } =
      name === t("common.default_folder_name")
        ? { isFolder: "folder" }
        : { isFolder: "folder", name: name, withoutCount: true };
    dispatch(clearRagObjects());
    dispatch(createComparisonObjectAction(postObj))
      .unwrap()
      .then((response: StorageObject<ComparisonObjectValue>) => {
        const newComparisonObjects = storage.addItem({
          ...response,
          children: [],
        });
        dispatch(
          setComparisonObjects(
            getFilteredObjects<ComparisonObjectValue>(newComparisonObjects),
          ),
        );
        navigate(getServicePagePath(response.id, "llm_compare"));
      });
  };

  const handleSelectComparisonObject = (id: string) => {
    const comparison = storage.findById(id);
    dispatch(
      setSelectedComparisonObject(
        comparison as StorageObject<ComparisonObjectValue>,
      ),
    );
    if (comparison?.value !== null) {
      dispatch(
        setComparisonObjectValue(
          comparison?.value?.llm_compare as CompareObject[],
        ),
      );
    }
    navigate(getServicePagePath(id, "llm_compare"));
  };

  return {
    selectedComparisonObject,
    sidebarComparisonItems,
    isComparisonLoading,
    comparisonObjects,
    folderItems,
    addComparisonObject,
    addFolder,
    handleSelectComparisonObject,
  };
};
